import { useCallback, useSyncExternalStore } from 'react'
import {
  cardWriteFailed, subscribeCardSnapshot, visibleCard,
} from '../lib/cardSnapshots'
import type { Card } from '../types'

/**
 * The card as it should be shown — the person's own unconfirmed change if there
 * is one, otherwise the freshest server answer (ADR-072 Decision 1 and 5).
 *
 * Every surface that renders a stored card's content goes through this: the
 * phone feed's collapsed rows, the desktop list rows, and the editing surface's
 * own seed. That is what makes "the list shows the change at once" true without
 * anything being painted into the deck list's query cache — the list is not
 * given a second copy of the card, it is pointed at the one the editor already
 * holds.
 *
 * `useSyncExternalStore` rather than an effect-and-state pair: the draft is
 * module state that changes outside React's knowledge, and this is the reader
 * React provides for exactly that. It also means a publication re-renders only
 * the rows for THAT card, not the feed.
 */
export function useVisibleCard(card: Card): Card {
  const id = card?.id ?? ''
  const subscribe = useCallback(
    (onChange: () => void) => subscribeCardSnapshot(id, onChange),
    [id],
  )
  // Returns a value from the module maps, so identity is stable between
  // publications and React's own bail-out applies.
  return useSyncExternalStore(subscribe, () => visibleCard(card), () => visibleCard(card))
}

/**
 * Is this card's change standing unsaved after a failed write (ADR-072
 * Decision 4)?
 *
 * True only after a failure. There is deliberately no "saving" state to read
 * here: while a write is merely unanswered the change looks exactly like a
 * confirmed one, because a mark on every edit — on a surface that commits every
 * time the caret leaves a field — would be the normal state of working on a
 * card and would therefore say nothing (Decision 3).
 */
export function useCardWriteFailed(cardId: string): boolean {
  const id = cardId ?? ''
  const subscribe = useCallback(
    (onChange: () => void) => subscribeCardSnapshot(id, onChange),
    [id],
  )
  return useSyncExternalStore(subscribe, () => cardWriteFailed(id), () => false)
}
